import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
// Relative, not `@/`: the API's integration spec loads this file at run time (console contract).
import { api } from '../../../lib/api/client';

// S20 · Notifications (07 §5): the API's `notifications` log, its silence
// windows and the two header actions, mirrored from
// apps/api/src/modules/notifications/notifications.controller.ts.
// Unknown keys pass through (`looseObject`).

const nullableString = z.string().nullable();

const buttonSchema = z.looseObject({
  text: z.string(),
  data: z.string().optional(),
  url: z.string().optional(),
});

/** One decision of the evaluator: sent, suppressed (with the reason) or failed. */
export const notificationSchema = z.looseObject({
  id: z.string(),
  channel: z.string(),
  kind: z.string(),
  shipment_id: nullableString,
  shipment_reference: nullableString.optional(),
  carrier_id: nullableString,
  carrier_code: nullableString.optional(),
  alert_id: nullableString,
  dedupe_key: nullableString,
  silence_until: nullableString.optional(),
  state: z.string(),
  suppressed_reason: nullableString,
  rendered_text: nullableString,
  buttons: z.array(z.array(buttonSchema)).nullable().catch(null),
  external_message_id: nullableString,
  sent_at: nullableString,
  error: nullableString,
  answered_at: nullableString,
  answer: nullableString,
  answered_by: nullableString,
  created_at: z.string(),
});
export type NotificationRow = z.infer<typeof notificationSchema>;

const countsSchema = z.looseObject({
  all: z.number(),
  sent: z.number(),
  suppressed: z.number(),
  failed: z.number(),
});

/** `GET /internal/notifications` */
export const notificationListSchema = z.looseObject({
  items: z.array(notificationSchema),
  channel_on: z.boolean(),
  counts: countsSchema.optional(),
});
export type NotificationList = z.infer<typeof notificationListSchema>;

/** `GET /internal/notifications/silences` */
export const silenceListSchema = z.looseObject({
  items: z.array(
    z.looseObject({
      dedupe_key: z.string(),
      kind: z.string(),
      shipment_id: nullableString,
      shipment_reference: nullableString,
      carrier_code: nullableString,
      since: z.string(),
      until: z.string(),
      notification_id: z.string(),
    }),
  ),
  quiet_hours: z.looseObject({ start: z.string(), end: z.string(), tz: z.string() }).nullable(),
  channel_on: z.boolean(),
});
export type SilenceList = z.infer<typeof silenceListSchema>;
export type Silence = SilenceList['items'][number];

/** `POST /internal/notifications/test|digest` (NotifyOutcome). */
export const notifyOutcomeSchema = z.looseObject({
  id: z.string(),
  state: z.enum(['sent', 'suppressed', 'failed']),
  reason: nullableString,
  counted: z.boolean(),
});
export type NotifyOutcome = z.infer<typeof notifyOutcomeSchema>;

/** `POST /internal/notifications/silences/end` */
export const endSilenceSchema = z.looseObject({ dedupe_key: z.string(), ended: z.number() });

export const STATE_FILTERS = ['all', 'sent', 'suppressed', 'failed'] as const;
export type StateFilter = (typeof STATE_FILTERS)[number];

/** The URL state of the tab (route `validateSearch`). */
export const notificationsSearchSchema = z.object({
  state: z.enum(STATE_FILTERS).optional().catch(undefined),
  /** The only channel is Telegram: the board's default filter shows it. `all` lifts it. */
  channel: z.enum(['telegram', 'all']).optional().catch(undefined),
  kind: z.string().max(48).optional().catch(undefined),
});
export type NotificationsSearch = z.infer<typeof notificationsSearchSchema>;

export interface NotificationsFilter {
  state: StateFilter;
  channel: 'telegram' | 'all';
  kind: string | null;
}

export const filterOf = (search: NotificationsSearch): NotificationsFilter => ({
  state: search.state ?? 'all',
  channel: search.channel ?? 'telegram',
  kind: search.kind ?? null,
});

export function listPath(filter: NotificationsFilter): string {
  const q = new URLSearchParams();
  if (filter.state !== 'all') q.set('state', filter.state);
  if (filter.channel !== 'all') q.set('channel', filter.channel);
  if (filter.kind) q.set('kind', filter.kind);
  q.set('limit', '200');
  return `/internal/notifications?${q.toString()}`;
}

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (filter: NotificationsFilter) => ['notifications', 'list', filter] as const,
  silences: ['notifications', 'silences'] as const,
};

export const notificationListQuery = (filter: NotificationsFilter) =>
  queryOptions({
    queryKey: notificationKeys.list(filter),
    queryFn: ({ signal }) => api(listPath(filter), { schema: notificationListSchema, signal }),
    refetchInterval: 60_000,
  });

export const silencesQuery = queryOptions({
  queryKey: notificationKeys.silences,
  queryFn: ({ signal }) => api('/internal/notifications/silences', { schema: silenceListSchema, signal }),
  refetchInterval: 60_000,
});

export const notificationsApi = {
  sendTest: (text: string) =>
    api('/internal/notifications/test', { method: 'POST', body: { text }, schema: notifyOutcomeSchema }),
  sendDigest: () => api('/internal/notifications/digest', { method: 'POST', schema: notifyOutcomeSchema }),
  endSilence: (dedupeKey: string) =>
    api('/internal/notifications/silences/end', {
      method: 'POST',
      body: { dedupe_key: dedupeKey },
      schema: endSilenceSchema,
    }),
};
